import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import React, { useEffect, useState, useCallback, useMemo } from 'react';

import OptimizedImage from '@/components/ui/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';

import {
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SIZE_SCALE,
  COLOR_SCALE,
  INTERACTIVE_STATES,
  SURFACE_PADDING,
  TYPE_SCALE,
  FONT_WEIGHTS,
  TEXT_ALIGNMENT,
  MARGIN_BOTTOM,
  DISPLAY,
  FLEX_ALIGN as FLEX_ALIGN,
  FLEX_JUSTIFY as FLEX_JUSTIFY,
} from './tokens';

import { Button } from './index';

// Types
type ImageVariant = 'default' | 'treatment' | 'doctor' | 'equipment' | 'provider';

interface ImageObject {
  url?: string;
  name?: string;
  [key: string]: unknown;
}

type ImageData = string | ImageObject;

interface FullscreenImageModalProps {
  // Required
  isOpen: boolean;
  onClose: () => void;

  // Image data
  images?: ImageData[];
  currentIndex?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;

  // Content customization
  title?: string;
  descriptions?: string[];
  _showDescriptionToggle?: boolean;

  // Styling variants
  variant?: ImageVariant;

  // Callbacks
  _onImageClick?: () => void;
}

// Zoom configuration
const ZOOM_CONFIG = {
  MIN: 0.5,
  MAX: 3,
  STEP: 0.25,
  DEFAULT: 1,
} as const;

// Custom hook for zoom and drag functionality
const useZoomAndDrag = (_isOpen: boolean, _onClose: () => void) => {
  const [zoomLevel, setZoomLevel] = useState<number>(ZOOM_CONFIG.DEFAULT);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + ZOOM_CONFIG.STEP, ZOOM_CONFIG.MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - ZOOM_CONFIG.STEP, ZOOM_CONFIG.MIN));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(ZOOM_CONFIG.DEFAULT);
    setDragOffset({ x: 0, y: 0 });
  }, []);


  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel <= 1) return;
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    },
    [zoomLevel, dragOffset.x, dragOffset.y]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || zoomLevel <= 1) return;
      e.preventDefault();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      setDragOffset({ x: deltaX, y: deltaY });
    },
    [isDragging, zoomLevel, dragStart.x, dragStart.y]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isDragging || zoomLevel <= 1 || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;
      setDragOffset({ x: deltaX, y: deltaY });
    },
    [isDragging, zoomLevel, dragStart.x, dragStart.y]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (zoomLevel <= 1 || e.touches.length !== 1) return;
      e.preventDefault();
      setIsDragging(true);
      const touch = e.touches[0];
      if (!touch) return;
      setDragStart({
        x: touch.clientX - dragOffset.x,
        y: touch.clientY - dragOffset.y,
      });
    },
    [zoomLevel, dragOffset.x, dragOffset.y]
  );

  const resetZoomAndDrag = useCallback(() => {
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  return {
    zoomLevel,
    dragOffset,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetZoomAndDrag,
  };
};

// Utility function to process image data
const processImageDataUtil = (
  images: ImageData[] | string[],
  descriptions: string[],
  currentIndex: number
) => {
  const imageUrls: string[] = Array.isArray(images)
    ? images.map(img => (typeof img === 'string' ? img : (img.url ?? '')))
    : [];
  const imageObjects = Array.isArray(images) ? images : [];
  const hasMultipleImages = imageUrls.length > 1;
  const safeDescriptions = Array.isArray(descriptions) ? descriptions : [];
  const isValidIndex =
    currentIndex >= 0 && currentIndex < Math.max(imageUrls.length, safeDescriptions.length);

  return {
    imageUrls,
    imageObjects,
    hasMultipleImages,
    safeDescriptions,
    isValidIndex,
  };
};

// Utility function to get current alt text
const getCurrentAltTextUtil = (
  imageObjects: ImageData[],
  safeDescriptions: string[],
  isValidIndex: boolean,
  currentIndex: number,
  title?: string
): string => {
  // Check descriptions first
  if (isValidIndex && safeDescriptions[currentIndex]) {
    return String(safeDescriptions[currentIndex]);
  }

  // Check image objects for name property
  if (isValidIndex) {
    const currentImage = imageObjects[currentIndex];
    if (currentImage && typeof currentImage === 'object' && 'name' in currentImage) {
      const name = (currentImage as { name?: string }).name;
      if (name) {
        return String(name);
      }
    }
  }

  // Fallback to generic title
  if (title) {
    return `${title} - Image ${currentIndex + 1}`;
  }
  return `Image ${currentIndex + 1}`;
};

// Custom hook for keyboard navigation
const useKeyboardNavigation = (
  hasMultipleImages: boolean,
  onNavigate: ((direction: 'prev' | 'next') => void) | undefined,
  onClose: () => void,
  handleZoomIn: () => void,
  handleZoomOut: () => void,
  handleZoomReset: () => void
) => {
  const handleNavigationKey = useCallback(
    (direction: 'prev' | 'next') => {
      if (hasMultipleImages && onNavigate) {
        onNavigate(direction);
      }
    },
    [hasMultipleImages, onNavigate]
  );

  const handleZoomKey = useCallback(
    (action: 'in' | 'out' | 'reset') => {
      switch (action) {
        case 'in':
          handleZoomIn();
          break;
        case 'out':
          handleZoomOut();
          break;
        case 'reset':
          handleZoomReset();
          break;
      }
    },
    [handleZoomIn, handleZoomOut, handleZoomReset]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          handleNavigationKey('prev');
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNavigationKey('next');
          break;
        case '+':
        case '=':
          event.preventDefault();
          handleZoomKey('in');
          break;
        case '-':
          event.preventDefault();
          handleZoomKey('out');
          break;
        case '0':
          event.preventDefault();
          handleZoomKey('reset');
          break;
      }
    },
    [onClose, handleNavigationKey, handleZoomKey]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { handleNavigationKey, handleZoomKey };
};

/**
 * Unified FullscreenImageModal component for consistent image viewing across the app
 *
 * Features:
 * - Single or multi-image navigation
 * - Optional description display with toggle
 * - Keyboard navigation (ESC, arrows)
 * - Zoom in/out functionality
 * - Image counter
 * - Accessibility support
 * - Design system consistency
 */
const FullscreenImageModal = ({
  // Required
  isOpen,
  onClose,

  // Image data
  images = [], // Array of image objects or URLs
  currentIndex = 0,
  onNavigate,

  // Content customization
  title, // Optional title (e.g., "Dr. Smith", "Treatment Name")
  descriptions = [], // Array of descriptions corresponding to images
  _showDescriptionToggle = false,

  // Styling variants
  variant = 'default', // 'default', 'treatment', 'doctor', 'equipment'

  // Callbacks
  _onImageClick,
}: FullscreenImageModalProps) => {
  const { t } = useTranslation();
  const [_isDescriptionVisible, _setIsDescriptionVisible] = useState(true);

  // Use custom hook for zoom and drag functionality
  const {
    zoomLevel,
    dragOffset,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetZoomAndDrag,
  } = useZoomAndDrag(isOpen, onClose);

  // Process images and get safe data
  const { imageUrls, imageObjects, hasMultipleImages, safeDescriptions, isValidIndex } =
    processImageDataUtil(images, descriptions, currentIndex);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (hasMultipleImages && onNavigate) {
      onNavigate('prev');
    }
  }, [hasMultipleImages, onNavigate]);

  const handleNext = useCallback(() => {
    if (hasMultipleImages && onNavigate) {
      onNavigate('next');
    }
  }, [hasMultipleImages, onNavigate]);

  const currentAltText = getCurrentAltTextUtil(
    imageObjects,
    safeDescriptions,
    isValidIndex,
    currentIndex,
    title
  );
  const _currentDescription = isValidIndex ? safeDescriptions[currentIndex] : '';

  // Use keyboard navigation hook
  useKeyboardNavigation(
    hasMultipleImages,
    onNavigate,
    onClose,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset
  );

  // Mouse and touch handlers

  // Touch handlers for mobile

  // Wheel zoom handler
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!isOpen) return;
      // Only handle wheel zoom when not over interactive elements
      if (
        e.target &&
        ((e.target as Element).closest('button') ||
          (e.target as Element).closest('[role="button"]'))
      )
        return;

      e.preventDefault();
      if (e.deltaY > 0) {
        handleZoomOut();
      } else {
        handleZoomIn();
      }
    },
    [isOpen, handleZoomIn, handleZoomOut]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleWheel, handleMouseMove, handleMouseUp]);

  // Reset description visibility and zoom when image changes
  useEffect(() => {
    _setIsDescriptionVisible(true);
    resetZoomAndDrag();
  }, [currentIndex, resetZoomAndDrag]);

  const variantClasses = useMemo(() => {
    switch (variant) {
      case 'doctor':
        return {
          closeButton: `${SURFACE_PADDING['2']} ${COLOR_SCALE.surface}/10 hover:${COLOR_SCALE.surface}/20 rounded-full text-white ${INTERACTIVE_STATES.transitions.all} z-10 backdrop-blur-sm`,
          navButton: `${SURFACE_PADDING['2']} rounded-full ${COLOR_SCALE.surface}/10 hover:${COLOR_SCALE.surface}/20 text-white ${INTERACTIVE_STATES.transitions.all}`,
          counterBg: 'bg-black/70',
          descriptionBg: 'bg-black/70 backdrop-blur-sm',
        };
      case 'equipment':
        return {
          closeButton: `absolute top-4 right-4 ${SURFACE_PADDING['2']} rounded-full ${COLOR_SCALE.surface}/10 hover:${COLOR_SCALE.surface}/20 text-white transition-colors`,
          navButton: `${SURFACE_PADDING['2']} rounded-full ${COLOR_SCALE.surface}/10 hover:${COLOR_SCALE.surface}/20 text-white transition-colors`,
          counterBg: 'bg-black/60',
          descriptionBg: 'bg-black/70 backdrop-blur-sm',
        };
      case 'provider': // New variant to match provider detail page style
        return {
          closeButton: `absolute top-4 right-4 ${SURFACE_PADDING['2']} bg-white/60 hover:bg-black/20 text-white ${RADIUS_SCALE.full} ${INTERACTIVE_STATES.transitions.colors} focus:outline-none z-20 backdrop-blur-sm`,
          navButton: `${SURFACE_PADDING['2']} rounded-full bg-white/60 hover:bg-black/20 text-white transition-colors backdrop-blur-sm`,
          counterBg: 'bg-white/70',
          descriptionBg: 'bg-white/70 backdrop-blur-sm',
        };
      default: // treatment and default
        return {
          closeButton: `absolute top-4 right-4 text-white ${COLOR_SCALE.surface}/20 hover:${COLOR_SCALE.surface}/40 backdrop-blur-sm border border-white/30 z-10 ${RADIUS_SCALE.full} transition-colors`,
          navButton: `text-white ${COLOR_SCALE.surface}/20 hover:${COLOR_SCALE.surface}/40 backdrop-blur-sm border border-white/30 z-10 ${RADIUS_SCALE.full} transition-colors`,
          counterBg: 'bg-black/70 backdrop-blur-sm',
          descriptionBg: 'bg-black/70 backdrop-blur-sm',
        };
    }
  }, [variant]);

  if (!isOpen || imageUrls.length === 0) return null;

  // Use different background and cursor based on variant and zoom state
  const backgroundClass = 'bg-black';
  const cursorClass =
    zoomLevel > 1
      ? 'cursor-grab active:cursor-grabbing'
      : variant === 'provider'
        ? 'cursor-pointer'
        : 'cursor-zoom-out';

  return (
    <div
      className={`fixed inset-0 z-[11001] ${backgroundClass} flex items-center justify-center ${cursorClass} group`}
      style={{ backgroundColor: '#000000' }}
      onClick={onClose}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      tabIndex={-1}
      role="button"
      aria-label={t('aria_labels', 'full_screen_viewer', 'Full screen image viewer')}
    >
      {/* Zoom controls - visible for provider and treatment variants */}
      {(variant === 'provider' || variant === 'treatment') && (
        <div
          className={`absolute top-4 left-4 flex items-center ${GAP_SCALE['2']} bg-white/60 backdrop-blur-md ${RADIUS_SCALE.xl} ${SURFACE_PADDING['2']} z-20`}
        >
          <button
            onClick={e => {
              e.stopPropagation();
              handleZoomOut();
            }}
            disabled={zoomLevel <= ZOOM_CONFIG.MIN}
            className={`bg-white/80 ${SURFACE_PADDING['2']} text-black hover:bg-black/20 active:bg-black/30 ${RADIUS_SCALE.full} disabled:opacity-40 disabled:cursor-not-allowed ${INTERACTIVE_STATES.transitions.colors} cursor-pointer backdrop-blur-sm`}
            title="Zoom out"
          >
            <ZoomOut className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`} />
          </button>

          <span
            className={`text-black ${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium} ${SIZE_SCALE.minW48} ${TEXT_ALIGNMENT.center}`}
          >
            {Math.round(zoomLevel * 100)}%
          </span>

          <button
            onClick={e => {
              e.stopPropagation();
              handleZoomIn();
            }}
            disabled={zoomLevel >= ZOOM_CONFIG.MAX}
            className={`bg-white/80 ${SURFACE_PADDING['2']} text-black hover:bg-black/20 active:bg-black/30 ${RADIUS_SCALE.full} disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer backdrop-blur-sm`}
            title="Zoom in"
          >
            <ZoomIn className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']}`} />
          </button>

          {zoomLevel !== ZOOM_CONFIG.DEFAULT && (
            <button
              onClick={e => {
                e.stopPropagation();
                handleZoomReset();
              }}
              className={`bg-white/80 text-black hover:bg-black/20 active:bg-black/30 ${RADIUS_SCALE.lg} text-xs ${SURFACE_PADDING['2']} transition-colors cursor-pointer backdrop-blur-sm aspect-square flex items-center justify-center`}
              title="Reset zoom"
            >
              Reset
            </button>
          )}
        </div>
      )}

      {/* Close button - hidden by default, shown on hover */}
      {variant === 'provider' ? (
        <button
          className={`${variantClasses.closeButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={t('common', 'close', 'Close')}
        >
          <X className={`${SIZE_SCALE.w6} ${SIZE_SCALE['6']}`} />
        </button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className={`${variantClasses.closeButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={t('common', 'close', 'Close')}
        >
          <X className={`${SIZE_SCALE.w6} ${SIZE_SCALE['6']}`} />
        </Button>
      )}

      {/* Navigation buttons */}
      {hasMultipleImages && (
        <>
          {variant === 'provider' ? (
            <>
              <button
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${variantClasses.navButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
                onClick={e => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                aria-label={t('common', 'previous_image', 'Previous image')}
              >
                <ChevronLeft className={`${SIZE_SCALE['8']}`} />
              </button>
              <button
                className={`absolute right-4 top-1/2 -translate-y-1/2 ${variantClasses.navButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
                onClick={e => {
                  e.stopPropagation();
                  handleNext();
                }}
                aria-label={t('common', 'next_image', 'Next image')}
              >
                <ChevronRight className={`${SIZE_SCALE['8']}`} />
              </button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${variantClasses.navButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
                onClick={e => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                aria-label={t('common', 'previous_image', 'Previous image')}
              >
                <ChevronLeft className={`${SIZE_SCALE['8']}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-4 top-1/2 -translate-y-1/2 ${variantClasses.navButton} opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity duration-200`}
                onClick={e => {
                  e.stopPropagation();
                  handleNext();
                }}
                aria-label={t('common', 'next_image', 'Next image')}
              >
                <ChevronRight className={`${SIZE_SCALE['8']}`} />
              </Button>
            </>
          )}
        </>
      )}

      {/* Image counter - hidden by default, shown on hover */}
      {hasMultipleImages && (
        <div
          className={`absolute top-4 left-1/2 -translate-x-1/2 ${variantClasses.counterBg} text-white ${PADDING_X['4']} ${PADDING_Y['2']} rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200`}
        >
          {currentIndex + 1} / {imageUrls.length}
        </div>
      )}

      {/* Title overlay - shown when title is provided */}
      {title && (
        <div
          className={`absolute bottom-4 left-4 ${variantClasses.descriptionBg} ${variant === 'provider' ? 'text-black' : 'text-white'} ${PADDING_X['4']} ${PADDING_Y['3']} rounded-lg backdrop-blur-md max-w-xs sm:max-w-sm md:max-w-md z-50`}
        >
          <h3
            className={`${TYPE_SCALE.base} sm:${TYPE_SCALE.lg} ${FONT_WEIGHTS.semibold} ${MARGIN_BOTTOM['1']} ${variant === 'provider' ? 'text-black' : 'text-white'}`}
          >
            {title}
          </h3>
          {safeDescriptions[currentIndex] && (
            <p
              className={`${TYPE_SCALE.xs} sm:${TYPE_SCALE.sm} opacity-90 leading-relaxed ${variant === 'provider' ? 'text-black' : 'text-white'}`}
            >
              {safeDescriptions[currentIndex]}{' '}
            </p>
          )}
        </div>
      )}

      {/* Main content - full viewport */}
      <div
        className={`${DISPLAY.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center} ${SIZE_SCALE.full} ${SIZE_SCALE.fullHeight}`}
      >
        {/* Image container with zoom support */}
        <div
          className={`relative flex items-center justify-center w-full flex-1 ${PADDING_X['4']} ${PADDING_Y['8']} transition-transform duration-200 ${
            zoomLevel > 1 ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          style={{
            transform: `scale(${zoomLevel}) translate(${dragOffset.x / zoomLevel}px, ${dragOffset.y / zoomLevel}px)`,
            transformOrigin: 'center center',
          }}
          onClick={e => {
            // Prevent closing when clicking on the image area
            e.stopPropagation();
          }}
          onKeyDown={e => {
            // Allow keyboard zoom reset
            if (e.key === '0' && zoomLevel !== ZOOM_CONFIG.DEFAULT) {
              e.preventDefault();
              e.stopPropagation();
              handleZoomReset();
            }
          }}
          tabIndex={zoomLevel > 1 ? 0 : -1}
          role="button"
          aria-label={`Zoomed image, current zoom level ${zoomLevel}x. Press 0 to reset zoom.`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <OptimizedImage
            src={isValidIndex && imageUrls[currentIndex] ? imageUrls[currentIndex] : ''}
            alt={currentAltText}
            width={1920}
            height={1080}
            priority={true}
            className={`max-w-full object-contain ${
              variant === 'provider'
                ? 'max-h-[calc(100vh-160px)] sm:max-h-[calc(100vh-180px)] md:max-h-[calc(100vh-200px)]'
                : 'max-h-full'
            } ${zoomLevel > 1 ? 'max-h-none max-w-none' : ''}`}
            objectFit="contain"
            sizes="100vw"
          />
        </div>
      </div>
    </div>
  );
};

export default FullscreenImageModal;
